import { RANGE_BOUNDS, RangeBound } from './RangeCatalog';

/**
 * Where one bound of one population's range comes from. See
 * my-ranges-spec.md §6.2.
 *
 * Attached to the BOUND, not the marker. The marker-level "Sources" link
 * (§5.0) answers "does ferritin have a bibliography"; a practitioner about to
 * raise the male Lab Min needs to know what that number in particular rests
 * on, which of the six tiers of claim it is, and why it is not the other
 * number they are holding. One link to a page does not have that conversation.
 *
 * A STATIC file, deliberately. These change when the literature changes, which
 * is a code change with a review attached — not a database write that nobody
 * sees, with a stale copy left in every snapshot under scripts/data/.
 */

/**
 * What kind of claim a number is. The most decision-relevant field on the
 * card, which is why it renders in the open rather than behind a second hover:
 * a guideline threshold, a population percentile and an outcome association
 * are three different things to disagree with.
 */
export type EvidenceKind =
  /** A central-95% interval of a reference population. */
  | 'reference-interval'
  /** Where a deficiency state physiologically begins. */
  | 'physiologic-threshold'
  /** ROC / Youden, optimized for sensitivity rather than for health. */
  | 'diagnostic-cutoff'
  /** A body's recommendation. */
  | 'guideline'
  /**
   * A practitioner text or professional recommendation. Its own tier because
   * it genuinely is one: the quality hierarchy runs outcome-based decision
   * limits, then reference intervals, then cut-offs from clinicians' opinions
   * and published professional recommendations. A reader deciding whether to
   * argue with a number should see which of those they are looking at.
   */
  | 'expert-opinion'
  /** Risk rises past here. */
  | 'outcome-association'
  /** Computed by LabSmarts from the lab range. Not cited, and says so. */
  | 'derived';

export const EVIDENCE_KIND_LABELS: Readonly<Record<EvidenceKind, string>> = {
  'reference-interval': 'Reference interval',
  'physiologic-threshold': 'Physiologic threshold',
  'diagnostic-cutoff': 'Diagnostic cutoff',
  guideline: 'Guideline',
  'expert-opinion': 'Expert opinion',
  'outcome-association': 'Outcome association',
  derived: 'Derived by LabSmarts',
};

export interface RangeSourceLink {
  /** "PubMed 42747316". Never "here" and never a bare url. */
  label: string;
  href: string;
}

export interface RangeSource {
  kind: EvidenceKind;
  /**
   * Author-year, for the save dialog, where the full citation would bury the
   * sentence it is attached to. "ASH 2026", "Mei 2021", "WHO 2020".
   */
  short: string;
  /** One plain sentence: what this number IS. Not the citation. */
  claim: string;
  /** Authors (max three, then et al.), title, journal, year. */
  citation: string;
  /** n, population, exclusions, method. An expert judges the cohort. */
  detail?: string;
  /**
   * A second source reaching the same number by a different method. Two
   * unrelated methods agreeing is the strongest thing a range can show, and
   * it is wasted unless it is set apart from the first citation.
   */
  corroboration?: {
    citation: string;
    detail?: string;
  };
  /**
   * The number a well-read practitioner is about to object with, named and
   * answered before they raise it. This is the part that turns an argument
   * into a conversation, and it is why the discrepancies are written down
   * rather than smoothed over.
   */
  counterpoint?: {
    question: string;
    answer: string;
  };
  links?: RangeSourceLink[];
}

const pubmed = (id: string): RangeSourceLink => ({
  label: `PubMed ${id}`,
  href: `https://pubmed.ncbi.nlm.nih.gov/${id}/`,
});

const doi = (id: string): RangeSourceLink => ({
  label: `doi ${id}`,
  href: `https://doi.org/${id}`,
});

// ─── Ferritin ────────────────────────────────────────────────────────────────
//
// The scope keys below are the ones the table has AFTER the 2026-10 ferritin
// range update (adult labMin on the CDC/Brittenham physiologic ladder, labMax
// on the WHO iron-overload thresholds, the adult female row split on menopause
// status). The sources and the numbers are ONE change: upload the ranges and
// these cards describe what is on screen; author them alone and a card will
// talk about 30 beside a row that still says 15.

const WHO_2020: Omit<RangeSource, 'claim'> = {
  kind: 'guideline',
  short: 'WHO 2020',
  citation:
    'WHO guideline on use of ferritin concentrations to assess iron status in individuals and populations. Geneva: World Health Organization; 2020. ISBN 978-92-4-000012-4.',
  detail:
    'The same guideline sets 500 µg/L for adults who are not apparently healthy, because inflammation raises ferritin independently of iron.',
  counterpoint: {
    question: 'Why not 400, the way a lab reports it?',
    answer:
      'Healthy-population studies put the male 97.5th percentile near 440 to 520 (Rodgers 2024; SENIORLAB 2026), higher still than a laboratory’s printed ceiling. That is a description of the population, not a statement that 440 is well. Lab Max here is an action threshold, deliberately tighter. Fewer than one in ten high ferritins is iron overload: % saturation at or above 45% points to overload, below it to an acute phase response.',
  },
  links: [
    {
      label: 'WHO 9789240000124',
      href: 'https://www.who.int/publications/i/item/9789240000124',
    },
    pubmed('40013463'),
  ],
};

const CALIPER: Omit<RangeSource, 'claim'> = {
  kind: 'reference-interval',
  short: 'CALIPER 2013',
  citation:
    'Bailey D, Colantonio D, Kyriakopoulou L, et al. Marked biological variance in endocrine and biochemical markers in childhood: establishment of pediatric reference intervals using healthy community children from the CALIPER cohort. Clin Chem. 2013;59(9):1393-1405.',
  detail:
    '1,482 healthy community children aged 2 days to 18 years, ethnically diverse, measured on the Abbott ARCHITECT i2000. Direct method under CLSI C28-A3, nonparametric 2.5th and 97.5th percentiles. Ferritin was one of the analytes needing extra partitioning inside the first year of life.',
  links: [pubmed('23637247')],
};

const DERIVED_OPTIMAL_MIN: RangeSource = {
  kind: 'derived',
  short: 'LabSmarts',
  claim:
    'One standard deviation in from the bottom of the lab range. No optimal floor is authored for this population, so LabSmarts calculates one.',
  citation: 'LabSmarts rule: optimalMin = labMin + (labMax − labMin) ÷ 4.',
  detail:
    'The physiologic thresholds that set the adult floor (20 µg/L at ages 1 to 4, about 24 at 5 to 14) are not authored on these rows. Until they are, the optimal band here is arithmetic, not evidence.',
};

const DERIVED_OPTIMAL_MAX: RangeSource = {
  kind: 'derived',
  short: 'LabSmarts',
  claim:
    'One standard deviation in from the top of the lab range. No optimal ceiling is authored for this population, so LabSmarts calculates one.',
  citation: 'LabSmarts rule: optimalMax = labMax − (labMax − labMin) ÷ 4.',
  detail:
    'The adult ceilings, 100 ng/mL in men and 75 in women, rest on cardiovascular and metabolic outcome data in adults and are not carried onto children.',
};

/**
 * The DEFAULTS, which are the pediatric case: nine of ferritin's fifteen rows
 * are children, so they answer through the marker-level key and only the three
 * adult and three pregnancy rows override it.
 */
const FERRITIN_MARKER_SOURCES: Partial<Record<RangeBound, RangeSource>> = {
  alarmMin: {
    kind: 'derived',
    short: 'LabSmarts',
    claim:
      'Half of Lab Min. Three standard deviations below the lab mean is a negative number for ferritin, so the usual derivation does not apply here.',
    citation: 'LabSmarts rule: alarmMin = labMin ÷ 2.',
    detail:
      'Applied to all fifteen ferritin populations. Stored rather than calculated at read time, so it does not move when the lab range is edited.',
  },
  alarmMax: {
    kind: 'derived',
    short: 'LabSmarts',
    claim:
      'Three standard deviations above the lab mean. LabSmarts calculates it, so editing the lab range moves it.',
    citation: 'LabSmarts rule: alarmMax = labMax + (labMax − labMin) ÷ 4.',
    detail:
      'No clinical threshold is authored above Lab Max for ferritin. A ferritin over 1000 ng/mL is the specialist-referral number, and even then only with liver iron confirmed by MRI.',
  },
  labMin: {
    ...CALIPER,
    claim:
      'The 2.5th percentile of healthy children this age. A population percentile, not a threshold at which anything happens.',
  },
  labMax: {
    ...CALIPER,
    claim: 'The 97.5th percentile of healthy children this age.',
  },
  optimalMin: DERIVED_OPTIMAL_MIN,
  optimalMax: DERIVED_OPTIMAL_MAX,
};

const ADULT_OPTIMAL_MIN: RangeSource = {
  kind: 'diagnostic-cutoff',
  short: 'Troike 2025',
  claim:
    'Where iron repletion is reliably established, rather than merely not yet deficient.',
  citation:
    'Troike KM, McShane AJ. Re-evaluating ferritin thresholds to diagnose iron deficiency. Clin Biochem. 2025;140:111020.',
  detail:
    'ROC analysis of iron-replete patients. Youden-optimal cutoffs 45 µg/L in females and 70 µg/L in males; diagnostic sensitivity in women improved by 44%.',
  corroboration: {
    citation:
      'Knovich MA, Storey JA, Coffman LG, et al. Ferritin for the clinician. Blood Rev. 2009;23(3):95-104.',
    detail:
      'Independently recommends a 40 µg/L screening cutoff where inflammation and infection are absent.',
  },
  counterpoint: {
    question: 'Why 45 and not 70 for men?',
    answer:
      'A Youden cutoff maximizes detection, not health. 45 is used for both sexes so that "below optimal" means the same thing on every chart, and so the male optimal band is not compressed to five units.',
  },
  links: [pubmed('41033542'), pubmed('18835072')],
};

/**
 * Optimal Max splits on sex as of 2026-10-11. It did not before, and the
 * single shared number was 75 on the strength of Friedrich 2009, which has
 * been removed: that paper reports NO significant association between ferritin
 * and cardiovascular disease in either sex, and concludes against the iron
 * hypothesis. The U-shape it describes is women-only and does not reach
 * significance, so the card was quoting a spline off a null result.
 *
 * No paper names 75. The relationship is continuous, so both ceilings below
 * are conservative placements on a gradient rather than thresholds anyone
 * has identified, and both say so.
 */
const ADULT_OPTIMAL_MAX_F: RangeSource = {
  kind: 'outcome-association',
  short: 'Choi 2005',
  claim:
    'Above this, ferritin is tracking inflammation and oxidative stress at least as often as it is tracking iron stores.',
  citation:
    'Choi KM, Lee KW, Kim HY, et al. Association among serum ferritin, alanine aminotransferase levels, and metabolic syndrome in Korean postmenopausal women. Metabolism. 2005;54(11):1510-1514.',
  detail:
    '959 postmenopausal women with no apparent cause of liver disease. Those with metabolic syndrome averaged 74.7 ng/mL against 59.6 without. Lecube 2008 separates the same two groups at 81.0 and 48.5 in 239 postmenopausal women. The ceiling sits where the affected group centers, not where risk has been shown to begin. Ahluwalia 2010 supplies the direction rather than the number: among adults free from subclinical inflammation, every 10 µg/L of ferritin carried 3% more risk of carotid atherosclerosis, a gradient with no inflection anywhere along it.',
  corroboration: {
    citation:
      'Iron Disorders Institute. Iron Tests. Organizational recommendation, not a peer-reviewed study.',
    detail:
      'Gives an optimal range of 25 to 75 ng/mL for both sexes, reached by clinical judgment rather than from a cohort. It is the oldest source behind this bound and the only one that names 75 outright, which is worth stating plainly: the cohort evidence above brackets the transition between roughly 50 and 80 without resolving where inside it to draw a line.',
  },
  counterpoint: {
    question: 'Why 75 for women when DePalma proposes 20 to 100 for adults?',
    answer:
      'DePalma does not split by sex and frames the range on cardiovascular mortality reduction. In women that endpoint points the other way: Kadoglou found the female mortality signal entirely at the LOW end, 2 to 44 ng/mL, with no association at the high end at all. The evidence that does separate women separates them lower, and both of those cohorts are postmenopausal. There is no premenopausal upper-bound evidence in the literature.',
  },
  links: [pubmed('16253641'), pubmed('20181783'), pubmed('34048587')],
};

const ADULT_OPTIMAL_MAX_M: RangeSource = {
  kind: 'outcome-association',
  short: 'Mainous 2009',
  claim:
    'Above this, the metabolic and fitness costs of stored iron start to show, well below anything a laboratory flags.',
  citation:
    'Mainous AG, Diaz VA. Relation of serum ferritin level to cardiovascular fitness among young men. Am J Cardiol. 2009;103(1):115-118.',
  detail:
    '1,030 men aged 20 to 49 from NHANES 1999 to 2002, treadmill-tested, adjusted for body mass index, anemia, smoking, exercise, blood donation and C-reactive protein. The reference group is 16 to 100 ng/mL. The 100 to 150 band did not differ from it, while 150 to 200 carried an odds ratio of 0.55 for high cardiovascular fitness. 45.5% of these men were above 150.',
  corroboration: {
    citation:
      'DePalma RG, Hayes VW, O’Leary TJ. Optimal serum ferritin level range: iron status measure and inflammatory biomarker. Metallomics. 2021;13(6):mfab030.',
    detail:
      'Reaches the same ceiling from mortality rather than fitness, proposing an optimal range of 20 to 100 ng/mL with transferrin saturation of 20 to 50%. A proposal offered for testing, not a cohort finding, but an independent route to 100.',
  },
  counterpoint: {
    question: 'Why 100 and not 200, where the mortality studies put the turn?',
    answer:
      '194 to 200 is where all-cause mortality turns in men (Kadoglou, Salonen, Ellervik), and that is a disease threshold rather than an optimum. The metabolic signal arrives far earlier: HOMA-IR is already raised from 87 µg/L upward in normoglycemic men (Nakamura 2016), and men who went on to develop diabetes averaged 96.6 ng/mL at baseline against 67.8 in controls while both sat inside the reference range (Forouhi 2007).',
  },
  links: [
    pubmed('19101240'),
    pubmed('34048587'),
    pubmed('27750351'),
    pubmed('17333112'),
  ],
};

/**
 * The newborn row spans two CALIPER partitions, which is why its two ends
 * look like they come from different places. They do.
 *
 * CALIPER splits the first year of life three ways on Abbott: 4 to <15 days
 * (99.6–717.0), 15 days to <6 months (14.0–647.2) and 6 months to <1 year
 * (8.4–181.9). LabSmarts ages in whole years, so age 0 is one row, and it
 * takes the LOWEST low (8.4, from 6 months to 1 year) and the upper limit of
 * the middle partition (647.2, from 15 days to 6 months). Traced 2026-10-07;
 * the card had said the number was unsourced until then.
 */
const NEWBORN_LAB_MAX: RangeSource = {
  ...CALIPER,
  short: 'CALIPER 2013',
  claim:
    'The 97.5th percentile for infants 15 days to 6 months old, which is the widest the first year gets once the newborn fortnight is past.',
  counterpoint: {
    question: 'Why is the low end from a different age band?',
    answer:
      'CALIPER partitions the first year three ways and LabSmarts has one row for it, so this row spans all three: the floor of 8 is the 2.5th percentile at 6 months to 1 year, and the ceiling of 647 is the 97.5th at 15 days to 6 months. A two-week-old can legitimately reach 717. Reading an infant ferritin against a band this wide finds only the extremes, which is the cost of whole-year age bands and not a defect in the numbers.',
  },
};

/*
 * EVERY PREGNANCY BOUND RESTS ON A PRIMARY SOURCE: a guideline, a cohort or a
 * physiologic threshold, each with a PMID. A practitioner text was consulted
 * while these were being worked out and reached the same floors independently,
 * which is why the numbers did not move when it came off the cards on
 * 2026-10-08 — nothing was standing on it. The cards name only the primary
 * literature, because that is what a practitioner questioning a range should
 * be handed, and a book they may not own is not an argument they can check.
 * The research trail is kept in docs/reference/.
 *
 * The 'expert-opinion' kind stays in the taxonomy with nothing using it. It is
 * the honest tier for a practitioner's own citation (see practitionerSource)
 * and for any future bound whose best source really is a professional
 * recommendation rather than a study.
 */

const PREGNANCY_LAB_MIN: Omit<RangeSource, 'claim'> = {
  kind: 'physiologic-threshold',
  short: 'Mei 2024',
  citation:
    'Mei Z, Addo OY, Jefferds MED, et al. Physiologically based trimester-specific serum ferritin thresholds for iron deficiency in US pregnant women. Blood Adv. 2024;8(14):3745-3753.',
  detail:
    '1,288 pregnant women aged 15 to 49, NHANES, with inflammation and possible liver disease excluded. Restricted cubic spline against hemoglobin and soluble transferrin receptor: 25.8 µg/L (18.1–28.5) in the first trimester, 18.3 (16.3–22.9) in the second, 19.0 (14.4–26.1) in the third.',
  counterpoint: {
    question: 'Why not 15, the way WHO writes it?',
    answer:
      'WHO recommends 15 µg/L for the first trimester on expert opinion and makes no recommendation at all for the second and third. On these thresholds an additional one pregnant woman in ten is recognized as iron deficient.',
  },
  links: [pubmed('38781318')],
};

const PREGNANCY_LAB_MAX: Omit<RangeSource, 'claim'> = {
  kind: 'reference-interval',
  short: 'Morton 2021',
  citation:
    "Morton A. Hematological normal ranges in pregnancy. Glob Libr Women's Med. 2021;8.",
  detail:
    'Ferritin falls across pregnancy as plasma volume expands and fetal demand rises, so each trimester carries its own ceiling: 133, 130 and 110 ng/mL.',
  links: [doi('10.3843/GLOWM.413403')],
};

const PREGNANCY_OPTIMAL_MIN_T1_T2: RangeSource = {
  kind: 'guideline',
  short: 'ASH 2026',
  claim:
    'Below this, iron deficiency is diagnosed in pregnancy. It is the threshold most clinicians reading a pregnancy result will be working from.',
  citation:
    'Powers JM, Lim MY, Achebe MO, et al. American Society of Hematology 2026 guidelines for diagnosis of iron deficiency. Blood Adv. 2026.',
  detail:
    'ASH recommends ≤30 µg/L in pregnancy and recommends AGAINST the older ≤15. For a pregnant patient who is already anemic it goes further, to ≤50. GRADE systematic review of evidence to July 2025.',
  corroboration: {
    citation:
      'Judistiani RTD, Madjid TH, Handono B, et al. First trimester ferritin is superior over soluble transferrin receptor and hepcidin in predicting anemia in the third trimester: result from a cohort study in Indonesia. Anemia. 2020;2020:8880045.',
    detail:
      'A cohort of 181 pregnant women in which a first-trimester ferritin at or below 27.23 ng/mL was the single best predictor of third-trimester anemia, outperforming soluble transferrin receptor and hepcidin. 30 sits just above that cut-point, so a woman heading for anemia later in pregnancy is already below optimal in the first trimester.',
  },
  counterpoint: {
    question: 'Why is this higher than the Lab Min of 26?',
    answer:
      'They answer different questions. 26 is where iron-deficient erythropoiesis measurably begins in the first trimester (Mei 2024, 25.8 µg/L). 30 is where the guideline draws diagnosis, deliberately a little more sensitive. Between the two a woman is inside the lab range and below optimal, which is exactly the signal that band exists to give.',
  },
  links: [pubmed('42747316'), pubmed('33101731')],
};

/**
 * The third trimester is the only one whose FLOOR moves, and 20 is where two
 * independent numbers meet.
 *
 * Mégier 2022 puts the normal nadir at 15 to 20 µg/L. Mei 2024 puts the
 * physiologic deficiency threshold for the third trimester at 19.0 µg/L, which
 * is also this row's labMin, so any optimal floor below 19 would sit under the
 * lab floor and break the ascending invariant the chart math depends on
 * (§5.2). 20 is the first value that clears both: the top of the normal nadir
 * band, and above the deficiency threshold rather than an arbitrary nudge.
 */
const PREGNANCY_OPTIMAL_MIN_T3: RangeSource = {
  kind: 'physiologic-threshold',
  short: 'Mégier 2022',
  claim:
    'Ferritin bottoms out between 35 and 38 weeks, and 15 to 20 ng/mL at that point is a normal presentation rather than a deficiency.',
  citation:
    'Mégier C, Peoc’h K, Puy V, Cordier AG. Iron metabolism in normal and pathological pregnancies and fetal consequences. Metabolites. 2022;12(2):129.',
  detail:
    'A review of iron metabolism through normal and complicated pregnancy. Ferritin falls consistently from 12 weeks to a nadir at 35 to 38 weeks, where 15 to 20 ng/mL is a normal finding. Mei 2024 puts the physiologic deficiency threshold for this trimester at 19.0 µg/L, which is this row’s lab minimum, so the optimal floor is 20: the top of the normal nadir and the first value clear of the deficiency threshold.',
  counterpoint: {
    question: 'Why is this lower than the other two trimesters?',
    answer:
      'Because the physiology is. Ferritin falls consistently from 12 weeks and reaches its lowest point between 35 and 38 weeks before rising again in the last month. A floor of 30 here would mark a woman at her nadir as below optimal when she is normal.',
  },
  links: [pubmed('35208204')],
};

/**
 * The ceiling, flat at 80 across all three trimesters.
 *
 * WHERE THE LEVEL COMES FROM, and it is worth being exact because the three
 * cards are not equally strong. Two first-trimester cohorts put a number on
 * it: Mo 2024's top quartile begins at 81.9 µg/L and is the only quartile
 * carrying raised gestational-diabetes risk, and Broekhuis 2024 found a term
 * median of 75.2 against 107.3 in women who delivered preterm. 80 sits between
 * them. Yang 2022 then shows the same direction holding at 16 to 18 AND 28 to
 * 32 weeks, the two windows every other cohort skips, but reports quartiles
 * rather than a threshold, so it carries the DIRECTION into the later
 * trimesters and not the level.
 *
 * Holding one ceiling across pregnancy is therefore a deliberate choice rather
 * than three separate findings, and each card says which it is. Nobody has
 * published a third-trimester ferritin threshold; what is published is that
 * higher within the normal range is worse, consistently, in every window
 * anyone has measured.
 */
const PREGNANCY_OPTIMAL_MAX_T1: RangeSource = {
  kind: 'outcome-association',
  short: 'Mo 2024',
  claim:
    'Above roughly this level in the first trimester, gestational diabetes and preterm birth both become more likely. Iron supplementation on top of it is the thing to reconsider.',
  citation:
    'Mo H, Wen J, Qu C, et al. Associations of maternal serum ferritin levels across gestation with gestational diabetes mellitus: a longitudinal cohort study. J Diabetes. 2024;16(11):e70027.',
  detail:
    '17,560 pregnancies, Shanghai, ferritin at 8.0–13.6 weeks. The top quartile begins at 81.9 µg/L and is the only one carrying a raised risk (adjusted OR 1.248, 95% CI 1.111–1.403). Women who started high and came down returned to baseline risk; those who stayed high had the highest incidence of all. In the authors’ words: “higher SF levels in early pregnancy were associated with an increased risk of GDM”.',
  corroboration: {
    citation:
      'Broekhuis A, Koenen SV, Broeren MAC, et al. High first trimester ferritin levels differ according to parity and are independently related to preterm birth. Acta Obstet Gynecol Scand. 2024;103(7):1437-1443.',
    detail:
      '2,044 healthy low-risk pregnancies, ferritin at 12 weeks on Roche Cobas e801. Median 75.2 µg/L in women who delivered at term against 107.3 in those who delivered preterm; odds ratio 2.5 for preterm birth in primiparous women and 6.2 in multiparous. A second cohort, a different outcome, and 80 falls between its two medians.',
  },
  counterpoint: {
    question: 'Is 80 a reference limit?',
    answer:
      'No. The 95th percentile of first-trimester ferritin is far higher — 190 µg/L in primiparous women and 129 in multiparous. 80 is where outcomes start to turn, not where the population ends, and two cohorts reach it independently, one from gestational diabetes and one from preterm birth.',
  },
  links: [pubmed('39523925'), pubmed('38650165')],
};

const PREGNANCY_OPTIMAL_MAX_T2: RangeSource = {
  kind: 'outcome-association',
  short: 'Yang 2022',
  claim:
    'The same ceiling as the first trimester. Elevated ferritin here raises the risk of gestational diabetes and of a small-for-gestational-age baby.',
  citation:
    'Yang L, Wu L, Liu Y, et al. Association between serum ferritin concentration and risk of adverse maternal and fetal pregnancy outcomes: a retrospective cohort study. Diabetes Metab Syndr Obes. 2022;15:2867-2876.',
  detail:
    '2,327 pregnancies in Guangdong, China, and the only cohort found that samples BOTH windows the others skip: 16–18 and 28–32 weeks. In the authors’ words, “the odds ratio of the highest quartile of SF concentration at 16-18th week of gestation was 1.43 (95% CI: 1.09, 1.89) for gestational diabetes mellitus (GDM) and 1.79 (95% CI: 1.15, 2.79) for small for gestational age (SGA) when compared with the lowest quartile”.',
  corroboration: {
    citation:
      'Mo H, Wen J, Qu C, et al. J Diabetes. 2024;16(11):e70027. · Broekhuis A, Koenen SV, Broeren MAC, et al. Acta Obstet Gynecol Scand. 2024;103(7):1437-1443.',
    detail:
      'The level itself comes from the first-trimester cohorts, which are the ones that put a number on it: a top quartile beginning at 81.9 µg/L in 17,560 pregnancies, and a term median of 75.2 against 107.3 preterm in 2,044. Yang shows the same direction still holds here.',
  },
  counterpoint: {
    question: 'Yang’s top quartile starts at 55.9, not 80. Why not use that?',
    answer:
      'Because a quartile boundary describes that cohort’s distribution, not a biological threshold — the same caveat applies to the 81.9 in the first-trimester study. Yang’s population ran lower throughout, and the paper itself shows how far these cut-points move: a Lebanese cohort whose top quartile began at 38.5 found no association at all. The direction transfers; the number does not.',
  },
  links: [pubmed('36160468')],
};

const PREGNANCY_OPTIMAL_MAX_T3: RangeSource = {
  kind: 'outcome-association',
  short: 'Yang 2022',
  claim:
    'Ferritin rising late in pregnancy runs against the normal physiologic fall and tracks inflammation rather than iron stores.',
  citation:
    'Yang L, Wu L, Liu Y, et al. Association between serum ferritin concentration and risk of adverse maternal and fetal pregnancy outcomes: a retrospective cohort study. Diabetes Metab Syndr Obes. 2022;15:2867-2876.',
  detail:
    'The only cohort that measured ferritin in this window, at 28–32 weeks in 2,327 pregnancies. In the authors’ words: “women with SF in the highest quartile had an increased risk of SGA (OR: 1.62; 95% CI: 1.01, 2.62)”, and “the lowest quartile of SF concentration decreased risk of SGA by 90%” among women with gestational diabetes. Their conclusion: “Maintaining an appropriately low level of maternal SF at 28-32th week of gestation in women with GDM could reduce the risk of SGA.”',
  counterpoint: {
    question: 'Why the same ceiling as the first trimester, when ferritin is falling?',
    answer:
      'Because the harm tracks being high for the stage of pregnancy, and no cohort has published a third-trimester threshold to replace it with. Yang reports quartiles rather than a cut-point, so it establishes the direction in this window, and the level stays the one the first-trimester cohorts put a number on: a top quartile beginning at 81.9 µg/L and a term median of 75.2 against 107.3 preterm. A falling optimal FLOOR already carries the physiology, 20 here against 30 earlier, so the band widens as pregnancy progresses rather than sliding down whole.',
  },
  links: [pubmed('36160468')],
};

/**
 * The physiologic floor for children, from the same CDC/Brittenham method that
 * sets the adult one. Three studies, three age groups, one approach: the serum
 * ferritin at which hemoglobin starts to fall and the markers of unmet iron
 * demand start to rise.
 */
const PED_OPTIMAL_MIN_1_4: RangeSource = {
  kind: 'physiologic-threshold',
  short: 'Mei 2021',
  claim:
    'Where iron-deficient erythropoiesis begins in young children. Below it, iron is no longer sufficient for hemoglobin synthesis.',
  citation:
    'Mei Z, Addo OY, Jefferds ME, et al. Physiologically based serum ferritin thresholds for iron deficiency in children and non-pregnant women. Lancet Haematol. 2021;8(8):e572-e582.',
  detail:
    '2,569 children aged 12 to 59 months, NHANES, with infection excluded. Hemoglobin identified 19.9 µg/L (95% CI 18.8–22.6); soluble transferrin receptor identified 20.0 (19.4–20.9). The band matches the study almost exactly: 12 to 59 months is ages 1 to 4.',
  corroboration: {
    citation:
      'Powers JM, Lim MY, Achebe MO, et al. ASH 2026 guidelines for diagnosis of iron deficiency. Blood Adv. 2026.',
    detail:
      'ASH adopted the same figure, recommending ≤20 µg/L instead of ≤12 for children nine months through four years.',
  },
  links: [pubmed('34329578'), pubmed('42747316')],
};

const PED_OPTIMAL_MIN_5_14: RangeSource = {
  kind: 'physiologic-threshold',
  short: 'Mei 2026',
  claim:
    'Where iron-deficient erythropoiesis begins in school-age children. It is higher than in toddlers because growth raises iron demand.',
  citation:
    'Mei Z, Addo OY, Jefferds MED, et al. Physiologically based ferritin thresholds for defining iron deficiency in US children aged 5 to 14 years. Blood Res Clin Innov. 2026;2(3):100093.',
  detail:
    '3,765 healthy children, NHANES III. The ferritin curve inflects at 23.9 µg/L (95% CI 21.7–27.3) against hemoglobin and 25.0 (23.8–27.1) against zinc protoporphyrin. The CDC threshold in force is ≤15.',
  // The journal is new enough that this article carries no PMID and no DOI
  // yet, so the link is the publisher's article page. Not a guessed DOI: the
  // 2025 companion has one, and inferring this one from its pattern would be
  // inventing an identifier.
  links: [
    {
      label: 'Blood Res Clin Innov 2;3:100093',
      href:
        'https://ashpublications.org/bloodrci/article/2/3/100093/568384/Physiologically-based-ferritin-thresholds-for',
    },
  ],
};

const PED_OPTIMAL_MIN_TEEN_M: RangeSource = {
  kind: 'physiologic-threshold',
  short: 'Mei 2025',
  claim:
    'From 15, boys take the adult male threshold. Puberty raises iron demand to the adult level before age does.',
  citation:
    'Mei Z, Addo OY, Jefferds MED, et al. Physiologically based serum ferritin thresholds for iron deficiency in US older adolescents and adults. Blood Res Clin Innov. 2025;2(1):100029.',
  detail:
    '5,169 men and 6,957 non-pregnant women aged 15 to 90, NHANES III, hemoglobin with erythrocyte zinc protoporphyrin. 33 µg/L in males from 15 up.',
  links: [doi('10.1016/j.brci.2025.100029')],
};

const PED_OPTIMAL_MIN_TEEN_F: RangeSource = {
  kind: 'physiologic-threshold',
  short: 'Mei 2021',
  claim:
    'Girls from 15 take the menstruating-women threshold, which is what the study population actually was.',
  citation:
    'Mei Z, Addo OY, Jefferds ME, et al. Lancet Haematol. 2021;8(8):e572-e582.',
  detail:
    'The non-pregnant cohort was women aged 15 to 49, so an adolescent girl is inside it rather than extrapolated to. Hemoglobin identified 25.2 µg/L, soluble transferrin receptor 24.0.',
  counterpoint: {
    question: 'What about a 14-year-old on this row?',
    answer:
      'The band is 14 to 18 and the studies split at 15: age 14 sits in the 5-to-14 cohort, whose threshold is about 24. One unit apart, so the band takes the figure that covers four of its five years.',
  },
  links: [pubmed('34329578')],
};

const FERRITIN_SCOPE_SOURCES: Record<string, RangeSource> = {
  // ── Newborns: the one bound nobody can trace ──────────────────────────────
  // ── Children: the physiologic floor, by the age group it was measured in ──
  'ferritin|M|1-4|-|-|-|-|-|-::optimalMin': PED_OPTIMAL_MIN_1_4,
  'ferritin|F|1-4|-|-|-|-|-|-::optimalMin': PED_OPTIMAL_MIN_1_4,
  'ferritin|M|5-13|-|-|-|-|-|-::optimalMin': PED_OPTIMAL_MIN_5_14,
  'ferritin|F|5-13|-|-|-|-|-|-::optimalMin': PED_OPTIMAL_MIN_5_14,
  'ferritin|M|14-15|-|-|-|-|-|-::optimalMin': {
    ...PED_OPTIMAL_MIN_5_14,
    counterpoint: {
      question: 'What about the 15-year-olds on this row?',
      answer:
        'The band is 14 to 15 and the studies split at 15: a 15-year-old boy sits in the 15-to-90 cohort, whose threshold is 33. The band takes the lower figure, which flags earlier rather than later.',
    },
  },
  'ferritin|M|16-18|-|-|-|-|-|-::optimalMin': PED_OPTIMAL_MIN_TEEN_M,
  'ferritin|F|14-18|-|-|-|-|-|-::optimalMin': PED_OPTIMAL_MIN_TEEN_F,

  'ferritin|M|0-0|-|-|-|-|-|-::labMax': NEWBORN_LAB_MAX,
  'ferritin|F|0-0|-|-|-|-|-|-::labMax': NEWBORN_LAB_MAX,

  // ── Adults ────────────────────────────────────────────────────────────────
  'ferritin|M|19-200|-|-|-|-|-|-::labMin': {
    kind: 'guideline',
    short: 'ASH 2026',
    claim: 'Below this, iron deficiency is diagnosed in adults.',
    citation:
      'Powers JM, Lim MY, Achebe MO, et al. American Society of Hematology 2026 guidelines for diagnosis of iron deficiency. Blood Adv. 2026.',
    detail:
      'GRADE systematic review of evidence to July 2025. Raises the adult threshold from ≤15 to ≤30 µg/L.',
    corroboration: {
      citation: 'Troike KM, McShane AJ. Clin Biochem. 2025;140:111020.',
      detail:
        'Independently derived 30 µg/L as the lower reference limit of iron-replete males, by a completely different method.',
    },
    counterpoint: {
      question: 'Why not 33?',
      answer:
        'Mei 2025 put the physiologic onset of iron-deficient erythropoiesis in men at 33 µg/L (5,169 men, NHANES III, hemoglobin with erythrocyte zinc protoporphyrin). LabSmarts uses 30 so the number matches the guideline a clinician is reading, and because an independent reference-interval method landed on the same 30.',
    },
    links: [pubmed('42747316'), pubmed('41033542')],
  },
  'ferritin|M|19-200|-|-|-|-|-|-::labMax': {
    ...WHO_2020,
    claim:
      'Above this there is a risk of iron overload in men and non-menstruating women.',
  },
  'ferritin|M|19-200|-|-|-|-|-|-::optimalMin': ADULT_OPTIMAL_MIN,
  'ferritin|M|19-200|-|-|-|-|-|-::optimalMax': ADULT_OPTIMAL_MAX_M,

  'ferritin|F|19-200|-|PRE|-|-|-|-::labMin': {
    kind: 'physiologic-threshold',
    short: 'Mei 2021',
    claim: 'Where iron-deficient erythropoiesis begins in menstruating women.',
    citation:
      'Mei Z, Addo OY, Jefferds ME, et al. Physiologically based serum ferritin thresholds for iron deficiency in children and non-pregnant women. Lancet Haematol. 2021;8(8):e572-e582.',
    detail:
      '7,498 non-pregnant women aged 15 to 49, NHANES, with infection, inflammation and liver disease excluded. Hemoglobin identified 25.2 µg/L (95% CI 24.2–26.2); soluble transferrin receptor identified 24.0 (23.3–24.6).',
    corroboration: {
      citation: 'Mei Z, Addo OY, Jefferds MED, et al. J Nutr. 2023;153(3):771-780.',
      detail:
        'Replicated on an earlier assay generation: 24.8 µg/L by hemoglobin and 22.5 by erythrocyte zinc protoporphyrin.',
    },
    counterpoint: {
      question: 'Why not 30, the way ASH writes it?',
      answer:
        'ASH gives one threshold for all adults. 25 is the measured onset in menstruating women specifically, from two independent indicators in the same cohort. Where the evidence separates the sexes, LabSmarts separates them.',
    },
    links: [pubmed('34329578'), pubmed('36803577')],
  },
  'ferritin|F|19-200|-|PRE|-|-|-|-::labMax': {
    ...WHO_2020,
    claim: 'Above this there is a risk of iron overload in menstruating women.',
  },
  'ferritin|F|19-200|-|PRE|-|-|-|-::optimalMin': ADULT_OPTIMAL_MIN,
  'ferritin|F|19-200|-|PRE|-|-|-|-::optimalMax': ADULT_OPTIMAL_MAX_F,

  'ferritin|F|19-200|-|POST|-|-|-|-::labMin': {
    kind: 'physiologic-threshold',
    short: 'Mei 2025',
    claim:
      'After menopause the threshold rises to match men’s. Menstrual loss is what made it lower.',
    citation:
      'Mei Z, Addo OY, Jefferds MED, et al. Physiologically based serum ferritin thresholds for iron deficiency in US older adolescents and adults. Blood Res Clin Innov. 2025;2(1):100029.',
    detail:
      '5,169 men and 6,957 non-pregnant women aged 15 to 90, NHANES III. 33 µg/L in men and postmenopausal women; 25 µg/L in premenopausal women.',
    counterpoint: {
      question: 'Which row does a client get?',
      answer:
        'Menopause status on the client record, not age. A woman who reached menopause at 44 is graded against this row from 44. With nothing entered, LabSmarts uses the premenopausal row.',
    },
    links: [doi('10.1016/j.brci.2025.100029')],
  },
  'ferritin|F|19-200|-|POST|-|-|-|-::labMax': {
    ...WHO_2020,
    claim: 'Non-menstruating women take the same overload threshold as men.',
  },
  'ferritin|F|19-200|-|POST|-|-|-|-::optimalMin': ADULT_OPTIMAL_MIN,
  'ferritin|F|19-200|-|POST|-|-|-|-::optimalMax': ADULT_OPTIMAL_MAX_F,

  // ── Pregnancy ─────────────────────────────────────────────────────────────
  'ferritin|F|-|1|-|-|-|-|-::labMin': {
    ...PREGNANCY_LAB_MIN,
    claim:
      'Where iron-deficient erythropoiesis begins in the first trimester.',
  },
  'ferritin|F|-|1|-|-|-|-|-::labMax': {
    ...PREGNANCY_LAB_MAX,
    claim: 'The upper reference limit for the first trimester.',
  },
  'ferritin|F|-|1|-|-|-|-|-::optimalMin': PREGNANCY_OPTIMAL_MIN_T1_T2,
  'ferritin|F|-|1|-|-|-|-|-::optimalMax': PREGNANCY_OPTIMAL_MAX_T1,

  'ferritin|F|-|2|-|-|-|-|-::labMin': {
    ...PREGNANCY_LAB_MIN,
    claim:
      'Where iron-deficient erythropoiesis begins in the second trimester. It is lower than the first because plasma volume has expanded.',
  },
  'ferritin|F|-|2|-|-|-|-|-::labMax': {
    ...PREGNANCY_LAB_MAX,
    claim: 'The upper reference limit for the second trimester.',
  },
  'ferritin|F|-|2|-|-|-|-|-::optimalMin': PREGNANCY_OPTIMAL_MIN_T1_T2,
  'ferritin|F|-|2|-|-|-|-|-::optimalMax': PREGNANCY_OPTIMAL_MAX_T2,

  'ferritin|F|-|3|-|-|-|-|-::labMin': {
    ...PREGNANCY_LAB_MIN,
    claim: 'Where iron-deficient erythropoiesis begins in the third trimester.',
  },
  'ferritin|F|-|3|-|-|-|-|-::labMax': {
    ...PREGNANCY_LAB_MAX,
    claim: 'The upper reference limit for the third trimester.',
  },
  'ferritin|F|-|3|-|-|-|-|-::optimalMin': PREGNANCY_OPTIMAL_MIN_T3,
  'ferritin|F|-|3|-|-|-|-|-::optimalMax': PREGNANCY_OPTIMAL_MAX_T3,
};

// ─── Vitamin D, 25-hydroxyvitamin D ──────────────────────────────────────────
//
// The second marker authored, 2026-10-23. Five rows, all five carrying the same
// six numbers: 30 / 50 / 80 / 100 in ng/mL with a stored alarmMax of 150, for
// male, female, and each trimester of pregnancy. There is no pediatric row, so
// the sex rows ARE the only rows, and editing one grades a four-year-old on it
// (RangeCatalog.isAdultScope, my-ranges-spec.md §5.1).
//
// THE FLOOR AND THE CEILING ARE NOT THE SAME KIND OF CLAIM, and the tiers say
// so. 50 rests on a measured physiologic endpoint: parathyroid suppression is
// not maximal until 40 ng/mL and above (Ginde 2012, 14,681 people), with two
// infection endpoints reaching about 50 independently. 80 rests on a
// professional recommendation, and nothing stronger exists for it. A reader
// deciding which number to argue with should be able to see that difference
// without reading both cards end to end, which is what `kind` is for.
//
// Note the floor is NOT justified on mortality, and the card says so. Every
// dose-response study of mortality flattens between 20 and 36 ng/mL, well
// below 50. That is a real discrepancy and it is written down rather than
// smoothed over.
//
// ON RAISING THE CEILING. Asked directly on 2026-10-23, and the answer was to
// keep 80. The cancer literature recommending 80 to 100 ng/mL is real,
// specific, and contested, and it is answered in the optimalMax counterpoint
// rather than left for the reader to discover. Two structural facts also
// constrain it: Lab Max is 100 because that is the interval Quest and LabCorp
// both print, and the invariant requires optimalMax < labMax, so a ceiling of
// 100 would need a lab ceiling that no laboratory reports. A result of 80 to
// 100 already reads as above optimal and inside range, which is the correct
// signal for a deliberately therapeutic level.
//
// SOURCED FROM THE BIBLIOGRAPHY PAGE, not independently of it. labsmarts.com/
// bibliography#vitamind is where these numbers were justified first, and four
// of its citations are now the primary sources here (Ginde, Borsche, Cannell,
// Ozkan). Its two Mercola.com articles and its GrassrootsHealth page are NOT
// carried over: the first two are not studies, and the "as high as 70-100
// ng/ml" line that the higher-ceiling case traces back to is one of them.

const VITD_ALARM_MIN: RangeSource = {
  kind: 'derived',
  short: 'LabSmarts',
  claim:
    'One standard deviation below the bottom of the lab range. LabSmarts calculates it, so editing the lab range moves it.',
  citation: 'LabSmarts rule: alarmMin = labMin − (labMax − labMin) ÷ 4.',
  detail:
    'Lands at 12.5 ng/mL against the stored lab range of 30 to 100. That is close to where the literature does carry a hard floor: below about 10 to 12 ng/mL is where rickets and osteomalacia appear, and where the published mortality curves are steepest. The agreement is a coincidence of the arithmetic, not an authored threshold, which is why this card says derived.',
};

const VITD_LAB_MIN: RangeSource = {
  kind: 'guideline',
  short: 'Endocrine Society 2011',
  claim:
    'Below this is insufficiency. A guideline decision threshold, not a percentile of any population.',
  citation:
    'Holick MF, Binkley NC, Bischoff-Ferrari HA, et al. Evaluation, treatment, and prevention of vitamin D deficiency: an Endocrine Society clinical practice guideline. J Clin Endocrinol Metab. 2011;96(7):1911-1930.',
  detail:
    'Set 20 ng/mL as deficiency and 30 as the floor of sufficiency, on calcium absorption and parathyroid suppression. It is the source of the 30 that most functional and integrative practice uses, and of the 30 on this row.',
  corroboration: {
    citation:
      'Quest Diagnostics test 17306 (Vitamin D, 25-Hydroxy, Total, Immunoassay) and LabCorp test 081950 (Vitamin D, 25-Hydroxy). Reported reference interval 30-100 ng/mL.',
    detail:
      'Both national laboratories report the same interval, so 30 is simultaneously the guideline threshold and the bottom of the printed range a client sees. Nothing has to be reconciled here, which is not true of most markers. The ERA and ESPN consensus (PubMed 39875204) independently targets above 30 ng/mL in kidney disease, fourteen years later and in a different specialty.',
  },
  counterpoint: {
    question: 'The Endocrine Society withdrew this in 2024. Why is it still 30?',
    answer:
      'The 2024 guideline declines to define any target concentration and suggests against routine 25(OH)D testing in the general population, because the randomized trials it prioritized enrolled mostly replete people and found little. It did not replace 30 with a lower number, it stopped naming one. The parathyroid and calcium-absorption physiology the 2011 figure rests on was not retracted, and the Institute of Medicine figure a laboratory prints, 20 ng/mL, is explicitly a bone-health floor for populations rather than a target for a person. 30 is the tighter of the two and the one a report should flag against.',
  },
  links: [pubmed('21646368'), pubmed('39875204'), pubmed('38828931')],
};

const VITD_OPTIMAL_MIN: RangeSource = {
  kind: 'physiologic-threshold',
  short: 'Ginde 2012',
  claim:
    'Above the concentration at which parathyroid suppression stops improving. A physiologic endpoint, measured rather than recommended.',
  citation:
    'Ginde AA, Wolfe P, Camargo CA, et al. Defining vitamin D status by secondary hyperparathyroidism in the U.S. population. J Endocrinol Invest. 2012;35(1):42-48.',
  detail:
    'NHANES 2003-2006, 14,681 participants aged 6 and over. Against a reference of 25(OH)D at or above 40 ng/mL, levels of 20 to 29.9 ng/mL were independently associated with PTH at or above 45 pg/ml (OR 2.0), while 30 to 39.9 ng/mL was not (OR 1.1). The authors conclude that optimal status by maximum PTH suppression "does not occur until at least 25OHD levels >=40 ng/ml". 50 sits above that with margin.',
  corroboration: {
    citation:
      'Borsche L, Glauner B, von Mendel J. COVID-19 mortality risk correlates inversely with vitamin D3 status, and a mortality rate close to zero could theoretically be achieved at 50 ng/mL 25(OH)D3. Nutrients. 2021;13(10):3596. Corroborated by Cannell JJ, Vieth R, Umhau JC, et al. Epidemic influenza and vitamin D. Epidemiol Infect. 2006;134(6):1129-1140.',
    detail:
      'Two infection endpoints reaching the same floor from a different direction: regression across eight clinical datasets puts the inflection near 50 ng/mL, and the influenza review recommends stabilizing at the concentration summertime sun produces, about 50 ng/mL. Both are weaker designs than the PTH analysis, which is why they corroborate rather than lead.',
  },
  counterpoint: {
    question: 'Mortality studies flatten around 30. Why is the floor 50?',
    answer:
      'Because those studies are answering a different question, and the distinction is worth holding onto. Pooled individual data on 26,916 people found mortality no lower above 75 nmol/L, 30 ng/mL, than within it, and the NHANES III nadir was about 32 ng/mL. Nothing in the MORTALITY literature earns 50. What earns it is parathyroid physiology, which is maximal only at 40 and above, plus the infection work. A practitioner who weights all-cause mortality most heavily should set this to 40, which is the figure the PTH analysis actually names, and the page exists so that is a setting rather than an argument.',
  },
  links: [
    pubmed('21606669'),
    pubmed('34684596'),
    pubmed('16959053'),
    pubmed('28207791'),
    pubmed('23666975'),
  ],
};

const VITD_OPTIMAL_MAX: RangeSource = {
  kind: 'expert-opinion',
  short: 'Wimalawansa 2025',
  claim:
    'The top of the functional target band. The least settled of the six numbers, and the one most worth reading before changing.',
  citation:
    'Wimalawansa SJ. Vitamin D’s impact on cancer incidence and mortality: a systematic review. Nutrients. 2025;17(14):2333.',
  detail:
    'Concludes that 25(OH)D above 40 ng/mL, in a band of 40 to 80, lowers cancer risk and mortality. 80 is the top of that band, and it is where LabSmarts stops calling a result optimal rather than a level anything is known to happen at.',
  counterpoint: {
    question:
      'Cancer research wants 80 to 100 ng/mL. Should the top of the band go up?',
    answer:
      'The evidence found on review in October 2026 does not support raising it, and gives two reasons to leave it where it is. First, the 80-to-100 recommendation comes from a specific body of work, chiefly pooled analyses of the GrassrootsHealth and Lappe cohorts, where women above 60 ng/mL had 80% less breast cancer than women below 20, and a modeled estimate that 80 ng/mL versus 10 would cut cancer incidence by about 70%. The two largest pooling projects disagree with it at exactly that concentration: across 17 cohorts and 5,706 colorectal cancers, risk stopped falling above 100 nmol/L, 40 ng/mL, and the reduction above it was not significant; across 17 cohorts and 10,484 breast cancers there was no association at any decile, including 100 to 125 nmol/L and above 125. Thirty-one Mendelian randomization studies find no causal effect on any cancer site. Prostate cancer runs the other way: nineteen prospective studies, 13,462 cases, found higher 25(OH)D associated with MORE total prostate cancer, odds ratio 1.22 for the highest fifth. Second, no randomized trial has ever tested a target of 80 to 100 ng/mL. The highest mean any major trial reached was about 48 ng/mL, and the one three-year trial run at and above the tolerable upper intake found dose-dependent LOSS of radial bone density at achieved means of 53 and 58 ng/mL, which is inside this band already. Supplementation does lower cancer MORTALITY by roughly 6% to 12% when given daily, which is the real finding in this literature, and it does not depend on reaching 80.',
  },
  // BOTH SIDES ARE CLICKABLE, deliberately. The first three argue FOR a higher
  // ceiling (the band itself, then the two GrassrootsHealth cohort analyses the
  // 80-to-100 case rests on); the last four are the counter-evidence named in
  // the counterpoint. A card that discussed the McDonnell papers without
  // linking them would be asking the reader to take the rebuttal on trust.
  links: [
    pubmed('40732958'),
    pubmed('29906273'),
    pubmed('27049526'),
    pubmed('29912394'),
    pubmed('36593337'),
    pubmed('30425058'),
    pubmed('31454046'),
  ],
};

const VITD_LAB_MAX: RangeSource = {
  kind: 'reference-interval',
  short: 'Quest / LabCorp',
  claim:
    'The top of the interval both national laboratories report. Above it is high, not toxic.',
  citation:
    'Quest Diagnostics test 17306 (Vitamin D, 25-Hydroxy, Total, Immunoassay) and LabCorp test 081950 (Vitamin D, 25-Hydroxy). Reported reference interval 30-100 ng/mL.',
  detail:
    'Checked on both test directories 2026-10-23. This is the number printed beside the client\u2019s result, which is why LabSmarts uses it rather than a figure of its own: a Lab Max that disagreed with the report in the client\u2019s hand would need explaining every time.',
  corroboration: {
    citation:
      'Dudenkov DV, Yawn BP, Oberhelman SS, et al. Changing incidence of serum 25-hydroxyvitamin D values above 50 ng/mL: a 10-year population-based study. Mayo Clin Proc. 2015;90(5):577-586. With Sha S, Gwenzi T, Chen LJ, et al. The safety profile of vitamin D supplements using real-world data from 445,493 participants of the UK Biobank. Nutrients. 2024;16(14):2251.',
    detail:
      'Of 20,308 measurements in Olmsted County, 0.2% of people exceeded 100 ng/mL, and across the whole range 25(OH)D was not significantly related to serum calcium or to the risk of hypercalcemia. In the UK Biobank, levels at or above 100 nmol/L were associated with neither hypercalcemia, nor kidney stones, nor arterial stiffness, adjusted for 49 covariates. So the printed ceiling is also where the population data stops showing anything.',
  },
  counterpoint: {
    question: 'Is nothing happening between 80 and 100?',
    answer:
      'Something measurable is, which is why the optimal band stops at 80 rather than following the lab range to 100. In two cohorts totaling about 500,000 people the calcium-phosphate product climbed sharply once 25(OH)D passed 50 ng/mL, which the authors read as a possible vascular calcification signal, and the same analysis put minimal C-reactive protein at 32 ng/mL and minimal white count at 42. The ERA and ESPN consensus names above 60 to 80 ng/mL as the range to avoid. None of that is toxicity and none of it makes a result above 80 abnormal, which is why Lab Max is still 100 and why a result of 80 to 100 reads as above optimal and inside range rather than as a problem.',
  },
  links: [pubmed('25939935'), pubmed('39064694'), pubmed('39203816'), pubmed('39875204')],
};

const VITD_ALARM_MAX: RangeSource = {
  kind: 'diagnostic-cutoff',
  short: 'Ozkan 2012',
  claim:
    'The concentration above which intoxication is diagnosed. Stored rather than calculated, so editing the lab range does not move it.',
  citation:
    'Ozkan B, Hatun S, Bereket A. Vitamin D intoxication. Turk J Pediatr. 2012;54(2):93-98.',
  detail:
    'States the threshold directly: "Serum 25OHD levels above 150 ng/ml are considered as VDI [vitamin D intoxication]." Intoxication is defined by hypercalcemia with raised 25(OH)D, low PTH, and high urine calcium to creatinine, so 150 is the level at which that workup becomes the question rather than a level at which harm is established.',
  corroboration: {
    citation:
      'Galior K, Grebe S, Singh R. Development of vitamin D toxicity from overcorrection of vitamin D deficiency: a review of case reports. Nutrients. 2018;10(8):953.',
    detail:
      'Thirteen case reports, and every patient presented between 150 and 1,220 ng/mL with serum calcium of 11.1 to 23.1 mg/dL. So the published case series begins exactly where the diagnostic threshold sits. Causes were manufacturing and labeling errors and prescribing mistakes, not ordinary supplementation.',
  },
  counterpoint: {
    question: 'Is 150 ng/mL actually dangerous?',
    answer:
      'On its own, usually not, and the alarm should be read as a prompt rather than a verdict. The mechanism needs much more: hypercalcemia arises when free 1,25-dihydroxyvitamin D is displaced, which Vieth puts at 25(OH)D above roughly 240 ng/mL, and the intakes behind published cases were 40,000 IU per day and up. What an alarm here should trigger is a serum calcium and a question about what the client is taking. It is also the one bound on this marker a practitioner is more likely to want LOWER than higher, since the calcium and inflammation signals start well below it.',
  },
  links: [pubmed('22734293'), pubmed('30042334')],
};

/**
 * The DEFAULTS, which here are every row: all five vitamin D populations carry
 * the same six numbers, so all five answer through the marker-level key and
 * only the pregnancy optimalMin overrides it.
 */
const VITAMIN_D_MARKER_SOURCES: Partial<Record<RangeBound, RangeSource>> = {
  alarmMin: VITD_ALARM_MIN,
  labMin: VITD_LAB_MIN,
  optimalMin: VITD_OPTIMAL_MIN,
  optimalMax: VITD_OPTIMAL_MAX,
  labMax: VITD_LAB_MAX,
  alarmMax: VITD_ALARM_MAX,
};

/**
 * Pregnancy is the one population where a floor above 40 ng/mL has a mechanism
 * behind it rather than a recommendation, so the three trimester rows get their
 * own card for that bound. Every other bound on those rows is the adult one.
 */
const VITD_PREGNANCY_OPTIMAL_MIN: RangeSource = {
  kind: 'physiologic-threshold',
  short: 'Wagner 2020',
  claim:
    'In pregnancy the conversion to the active hormone is limited by how much 25(OH)D is available, and stops being limited around 40 ng/mL.',
  citation:
    'Wagner CL, Hollis BW. Early-life effects of vitamin D: a focus on pregnancy and lactation. Ann Nutr Metab. 2020;76(Suppl 2):16-28.',
  detail:
    'Circulating 1,25-dihydroxyvitamin D rises to two or three times the pre-pregnant level early in gestation. At no other point in life is its production directly proportional to 25(OH)D, and that relationship is described as optimized at or above 100 nmol/L, which is 40 ng/mL. It is the clearest substrate argument for a higher floor anywhere in the vitamin D literature, and it applies only here.',
  corroboration: {
    citation:
      'Kramer CK, Swaminathan B, Hanley AJ, et al. The relationship between parathyroid hormone and 25-hydroxyvitamin D during and after pregnancy. J Clin Endocrinol Metab. 2016;101(5):1965-1972.',
    detail:
      '468 women measured in late pregnancy and twice postpartum. Parathyroid hormone begins to rise below 82 nmol/L, about 33 ng/mL, in pregnancy, and the thresholds in and after pregnancy were not statistically different.',
  },
  counterpoint: {
    question: 'Then why is the floor 50 rather than 40?',
    answer:
      'Because the pregnancy rows carry the adult band unchanged, and the adult floor is 50. The two anchors above are 40 and 33, so 50 is above both of them, deliberately on the safe side for a population where deficiency is linked to preeclampsia, preterm birth, and gestational diabetes. A practitioner who wants the trimester rows to sit at the figure the physiology actually names should set them to 40, and these three rows can be set separately from the non-pregnant one.',
  },
  links: [pubmed('33232956'), pubmed('26895390')],
};

const VITAMIN_D_SCOPE_SOURCES: Record<string, RangeSource> = {
  'vitaminD|F|-|1|-|-|-|-|-::optimalMin': VITD_PREGNANCY_OPTIMAL_MIN,
  'vitaminD|F|-|2|-|-|-|-|-::optimalMin': VITD_PREGNANCY_OPTIMAL_MIN,
  'vitaminD|F|-|3|-|-|-|-|-::optimalMin': VITD_PREGNANCY_OPTIMAL_MIN,
};

/**
 * Every authored source, keyed `<scopeKey>::<bound>` for one population and
 * `<attribute>::<bound>` for every population of a marker.
 *
 * The two-level key is what keeps this from becoming a data-entry project
 * across 3,285 rows: ferritin's alarmMin rule is written once, and only the
 * four bounds that genuinely differ by population are written per population.
 */
const markerLevel = (
  attribute: string,
  sources: Partial<Record<RangeBound, RangeSource>>,
): Record<string, RangeSource> =>
  Object.keys(sources).reduce(
    (all, bound) => ({
      ...all,
      [`${attribute}::${bound}`]: sources[bound as RangeBound] as RangeSource,
    }),
    {} as Record<string, RangeSource>,
  );

export const RANGE_SOURCES: Readonly<Record<string, RangeSource>> = {
  ...markerLevel('ferritin', FERRITIN_MARKER_SOURCES),
  ...FERRITIN_SCOPE_SOURCES,
  ...markerLevel('vitaminD', VITAMIN_D_MARKER_SOURCES),
  ...VITAMIN_D_SCOPE_SOURCES,
};

/**
 * A link for a PubMed ID or a DOI, or nothing when it is neither.
 *
 * Deliberately forgiving about what gets pasted — "PMID: 34329578",
 * "https://doi.org/10.1234/x" and a bare number all resolve — and
 * deliberately silent when it cannot tell. A practitioner typing a page
 * number into the identifier box should get no link, not a broken one.
 */
export function identifierLink(identifier?: string): RangeSourceLink | undefined {
  const raw = String(identifier || '').trim();
  if (!raw) {
    return undefined;
  }
  const pmid = raw.replace(/^(pmid|pubmed)[:\s]*/i, '').trim();
  if (/^\d{4,9}$/.test(pmid)) {
    return pubmed(pmid);
  }
  const doiBody = raw
    .replace(/^https?:\/\/(dx\.)?doi\.org\//i, '')
    .replace(/^doi[:\s]*/i, '')
    .trim();
  if (/^10\.\d{4,9}\/\S+$/.test(doiBody)) {
    return doi(doiBody);
  }
  return undefined;
}

/**
 * The practitioner's own note, rendered through the same card as everything
 * else. See my-ranges-spec.md §6.2.
 *
 * `expert-opinion` is the honest tier, and the reason that rung was kept once
 * every shipped bound had a primary source behind it: a practitioner's own line
 * sits exactly where a published professional recommendation does, below a
 * cohort and above nothing.
 */
export function practitionerSource(
  note?: string,
  identifier?: string,
): RangeSource | undefined {
  const text = String(note || '').trim();
  const link = identifierLink(identifier);
  if (!text && !link) {
    return undefined;
  }
  return {
    kind: 'expert-opinion',
    short: 'your source',
    claim: text || 'Recorded against the reference below.',
    // No citation line: the practitioner's sentence IS the citation, and an
    // empty one renders as a blank paragraph rather than as nothing.
    citation: '',
    links: link ? [link] : [],
  };
}

/** The marker a scope key belongs to. scopeKeyFor() puts it first. */
function attributeOf(scopeKey: string): string {
  return String(scopeKey || '').split('|')[0];
}

/**
 * Where this number comes from, or undefined.
 *
 * Resolution falls back, most specific first:
 *
 *   1. `<scopeKey>::<bound>`  — this population's own source
 *   2. `<attribute>::<bound>` — the same source for every population
 *   3. nothing                — the caller shows no chip, and the
 *                               marker-level bibliography link still stands
 *
 * Step 3 is the point: a marker with no entries behaves exactly as it did
 * before this existed, so the allowlist can widen one marker at a time.
 */
export function rangeSourceFor(
  scopeKey: string,
  bound: RangeBound,
): RangeSource | undefined {
  if (!scopeKey || !bound) {
    return undefined;
  }
  return (
    RANGE_SOURCES[`${scopeKey}::${bound}`] ||
    RANGE_SOURCES[`${attributeOf(scopeKey)}::${bound}`]
  );
}

/** Whether any bound of any of these populations has a source to show. */
export function hasRangeSources(scopeKeys: string[] = []): boolean {
  return (scopeKeys || []).some(scopeKey =>
    RANGE_BOUNDS.some(bound => !!rangeSourceFor(scopeKey, bound)),
  );
}

export interface RangeSourceGroupEntry {
  source: RangeSource;
  /** The populations this one source answers for, in the order given. */
  appliesTo: string[];
}

export interface RangeSourceGroup {
  bound: RangeBound;
  entries: RangeSourceGroupEntry[];
}

/**
 * The sources for a marker's populations, grouped by BOUND rather than by
 * population, for the Sources panel.
 *
 * Grouping this way is both more compact and more honest: Lab Min collects
 * four citations across three populations while Alarm Min and Alarm Max
 * collect none, and seeing that two of the six numbers are LabSmarts'
 * arithmetic is better than implying all six are cited.
 *
 * Entries dedupe by object identity, which is exactly right: the fallback
 * hands every population the SAME source object for a marker-level bound, so
 * one entry comes back listing all of them.
 */
export function rangeSourceGroupsFor(
  rows: Array<{ scopeKey: string; label: string }> = [],
): RangeSourceGroup[] {
  const groups: RangeSourceGroup[] = [];
  RANGE_BOUNDS.forEach(bound => {
    const entries: RangeSourceGroupEntry[] = [];
    (rows || []).forEach(row => {
      const source = rangeSourceFor(row.scopeKey, bound);
      if (!source) {
        return;
      }
      const already = entries.find(entry => entry.source === source);
      if (already) {
        already.appliesTo.push(row.label);
        return;
      }
      entries.push({ source, appliesTo: [row.label] });
    });
    if (entries.length > 0) {
      groups.push({ bound, entries });
    }
  });
  return groups;
}
